import { useEffect, useRef, useState } from 'react'
import { Copy, Check } from 'lucide-react'
import { EMPTY } from '@/lib/customer-format'

/**
 * A monospace identifier (e.g. a Stripe ID) with a one-click copy button.
 * Null/empty → a neutral em dash. Degrades silently where the Clipboard API
 * isn't available (e.g. an insecure context).
 */
export function CopyableId({ value }: { value: string | null | undefined }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  if (!value) return <span className="text-cmd-outline">{EMPTY}</span>

  const onCopy = () => {
    if (!navigator.clipboard) return
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true)
        if (timer.current) clearTimeout(timer.current)
        timer.current = setTimeout(() => setCopied(false), 1500)
      })
      .catch(() => {
        /* clipboard blocked — leave the value visible for manual copy */
      })
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="break-all font-mono text-xs text-white/90">{value}</span>
      <button
        type="button"
        onClick={onCopy}
        aria-label={copied ? 'Copied' : `Copy ${value}`}
        className="shrink-0 text-cmd-outline transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-[#80ffb4]" aria-hidden="true" />
        ) : (
          <Copy className="h-3.5 w-3.5" aria-hidden="true" />
        )}
      </button>
    </span>
  )
}
